import { useRef } from "react";
import { PAGE_X, PAGE_Y } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import type { SchemeTokens } from "@shared/types/foyer";
import { terraManifest } from "@shared/schemes/manifests";
import { SchemeProvider } from "../_shared/schemeContext";
import { useScreenFocus } from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { LauncherIcon } from "../_shared/launcher/icons";
import {
  LauncherPage,
  LauncherCustom,
  LauncherTv,
  useHomeNav,
  useLauncherClock,
  visibleFunctions,
  homeBgLayers,
  greeting,
  useGuestName,
  welcomeLine,
  SchemeSlot,
  launcherSlotStyle,
  type LauncherTheme,
  type LauncherScreen,
} from "../_shared/launcher/kit";
import { useNow } from "../_shared/useNow";
import { offerSlotBanner } from "../_shared/slots/offerSlot";
import type { SchemeProps } from "../registry";

/** Width of the rail's content box (logo, menu, footer icons). */
const RAIL_CONTENT = 152;

/**
 * Terra - warm boutique. ARCHETYPE: a persistent slim left icon sidebar + a
 * right area showing the property photo (home) or the active sub-page on a warm
 * cream surface. Terracotta + cream, Spectral / DM Sans.
 */
function terraTheme(
  t: SchemeTokens
): LauncherTheme & { rootBg: string; panel: string } {
  const e = { ...terraManifest.defaultTokens.extras, ...t.extras };
  return {
    serif: t.fonts.serif ?? "",
    sans: t.fonts.sans ?? "",
    surface: e.surface,
    card: e.card,
    chip: e.chip,
    line: t.palette.rule ?? "",
    ink: t.palette.ink,
    muted: t.palette.inkDim ?? "",
    accent: t.palette.accent,
    onAccent: e.onAccent,
    phTone: e.phTone,
    phInk: e.phInk,
    photoTone: "light",
    rootBg: t.palette.bg,
    panel: e.panel,
  };
}

export default function TerraScheme({ scheme }: SchemeProps) {
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  const [screen, setScreen] = useTrackedScreen<LauncherScreen>("home");
  useBackHome(() => setScreen("home"));
  const T = terraTheme(scheme.tokens);
  const c = useLauncherClock();
  const guest = useGuestName();
  const { hotelName } = scheme.content.branding;
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  const funcs = visibleFunctions(scheme);
  // The home promo slot, when it holds a live offer (`POS-4`). Resolved here
  // because the dock's focus chain needs to know whether there is a cell to
  // add, and `SchemeSlot` needs to know whether to draw a ring on it - one
  // answer behind both, or the remote and the picture disagree.
  // Read off the scheme directly rather than through `useOfferSlot`: this
  // component body sits OUTSIDE its own `SchemeProvider` (the provider is in
  // the tree it returns), so the context hook has nothing to read here.
  const promoClock = useNow();
  const promoBanner = offerSlotBanner(scheme, "homePromo", promoClock);
  const home = useHomeNav({
    funcs,
    screen,
    go: setScreen,
    scopeRef,
    tvPlayer: scheme.context.tvPlayer,
    headerFirst: false,
    // Last in the chain, and only while there is a live offer: an offer nobody
    // asked for may interrupt the eye, never the remote, so the ring still
    // starts on the dock.
    promo: {
      active: !!promoBanner,
      onPress: () => setScreen("offers"),
    },
  });
  const isHome = screen === "home";
  const clay = T.accent;
  const ink = T.ink;
  const cream = T.onAccent;

  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: T.rootBg,
          color: ink,
          overflow: "hidden",
          fontFamily: T.sans,
          display: "flex",
          outline: "none",
        }}
      >
        {/* left icon rail (persistent) */}
        <div
          style={{
            // The left inset is title-safe (overscan); the right one mirrors
            // it so the menu sits in the middle of the rail. The rail is
            // widened to keep the same ~150px content box the labels need.
            width: PAGE_X * 2 + RAIL_CONTENT,
            background: T.panel,
            borderRight: `1px solid ${T.line}`,
            padding: `${PAGE_Y}px ${PAGE_X}px`,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: "0 0 auto",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 99,
                border: `1.5px solid ${clay}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
              }}
            >
              <span
                style={{
                  fontFamily: T.serif,
                  fontSize: 26,
                  fontWeight: 600,
                  color: clay,
                }}
              >
                {(hotelName[0] || "T").toUpperCase()}
              </span>
            </div>
            <div style={{ fontFamily: T.serif, fontSize: 24, fontWeight: 600 }}>
              {hotelName}
            </div>
          </div>

          {isHome && (
            <div
              style={{
                marginTop: 64,
                display: "flex",
                flexDirection: "column",
                gap: 14,
                width: "100%",
                padding: 0,
                boxSizing: "border-box",
              }}
            >
              {funcs.map((f, i) => {
                const on = home.funcIdx === i;
                return (
                  <div
                    key={f.key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 9,
                      padding: "18px 0",
                      borderRadius: 16,
                      background: on ? clay : "transparent",
                    }}
                  >
                    <LauncherIcon
                      name={f.icon}
                      size={30}
                      color={on ? cream : ink}
                      stroke={1.6}
                    />
                    <span
                      style={{
                        fontFamily: T.sans,
                        fontSize: 14.5,
                        fontWeight: on ? 600 : 500,
                        letterSpacing: "0.02em",
                        color: on ? cream : ink,
                        textAlign: "center",
                        lineHeight: 1.15,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              marginTop: "auto",
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            {home.headerIcons.map((name, k) => {
              const on = isHome && home.headerIdx === k;
              return (
                <div
                  key={name}
                  style={{
                    padding: 7,
                    borderRadius: 10,
                    display: "flex",
                    background: on ? clay : "transparent",
                    opacity: on ? 1 : 0.6,
                  }}
                >
                  <LauncherIcon
                    name={name}
                    size={22}
                    color={on ? cream : ink}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* right area */}
        {isHome ? (
          <div
            style={{
              flex: 1,
              position: "relative",
              backgroundColor: bgL.photo && hero ? "#000" : T.phTone,
              backgroundImage: bgL.photo
                ? hero
                  ? `url(${hero})`
                  : `repeating-linear-gradient(135deg, ${T.phInk} 0 1px, transparent 1px 11px)`
                : undefined,
              backgroundSize: bgL.photo && hero ? "cover" : undefined,
              backgroundPosition: "center",
            }}
          >
            {bgL.tint && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(40,28,20,0.34) 0%, transparent 22%, transparent 58%, rgba(40,28,20,0.62) 100%)",
                }}
              />
            )}
            <div
              style={{
                position: "absolute",
                top: PAGE_Y,
                right: PAGE_X,
                display: "flex",
                alignItems: "center",
                gap: 16,
                color: cream,
              }}
            >
              <LauncherIcon name="cloud" size={26} color={cream} />
              <span style={{ fontFamily: T.serif, fontSize: 32 }}>21°</span>
              <span
                style={{
                  width: 1,
                  height: 24,
                  background: "rgba(241,232,219,0.5)",
                }}
              />
              <span style={{ fontFamily: T.serif, fontSize: 32 }}>
                {c.time24}
              </span>
            </div>
            {/* operator promo slot - right side, below the weather chrome */}
            <SchemeSlot
              focused={home.promoFocused}
              onPress={() => setScreen("offers")}
              slotId="homePromo"
              style={launcherSlotStyle(T)}
              containerStyle={{
                position: "absolute",
                top: 130,
                right: PAGE_X,
                width: 400,
                zIndex: 2,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: PAGE_X,
                bottom: 52,
                color: cream,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  letterSpacing: "0.26em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  opacity: 0.85,
                  marginBottom: 10,
                }}
              >
                {welcomeLine(greeting(), guest)}
              </div>
              <div
                style={{
                  fontFamily: T.serif,
                  fontSize: 46,
                  fontWeight: 600,
                  lineHeight: 1.02,
                }}
              >
                Tonight · garden dinner under the figs, 19:30
              </div>
            </div>
          </div>
        ) : screen === "tv" ? (
          // Full-bleed, before LauncherPage: the built-in player carries its
          // own left-hand menu and must own the whole panel, exactly like the
          // vendor app it replaces.
          <LauncherTv theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : screen === "custom" ? (
          <LauncherCustom theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : (
          <div style={{ flex: 1, position: "relative" }}>
            <LauncherPage
              screen={screen}
              theme={T}
              go={setScreen}
              scopeRef={scopeRef}
            />
            <div
              style={{
                position: "absolute",
                top: 52,
                right: PAGE_X,
                display: "flex",
                alignItems: "center",
                gap: 14,
                color: ink,
              }}
            >
              <LauncherIcon name="cloud" size={24} color={ink} />
              <span style={{ fontFamily: T.serif, fontSize: 28 }}>21°</span>
              <span
                style={{
                  width: 1,
                  height: 22,
                  background: "rgba(58,45,36,0.3)",
                }}
              />
              <span style={{ fontFamily: T.serif, fontSize: 28 }}>
                {c.time24}
              </span>
            </div>
          </div>
        )}
      </div>
    </SchemeProvider>
  );
}
